// Meta Pixel (fbq) helpers. The pixel itself is mounted by
// <MetaPixel /> in (marketing) and (platform)/join layouts.
//
// Every event carries an `event_id` (UUID v4) so the matching server-side
// Conversions API call (issue ENG-340) can dedupe the pair on Meta's side.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export type MetaStandardEvent =
  | "PageView"
  | "ViewContent"
  | "Lead"
  | "CompleteRegistration";

export interface TrackMetaInput {
  event: MetaStandardEvent;
  params?: Record<string, unknown>;
  eventId?: string;
}

export function generateMetaEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Vanishingly rare path: browsers without crypto.randomUUID. Still good
  // enough as a dedupe key; collision risk is irrelevant for ads tracking.
  return (
    Math.random().toString(36).slice(2) +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2)
  );
}

export function trackMeta({
  event,
  params = {},
  eventId,
}: TrackMetaInput): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  if (typeof window.fbq !== "function") {
    return null;
  }
  const id = eventId ?? generateMetaEventId();
  window.fbq("track", event, params, { eventID: id });
  return id;
}
